import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/common/Button';
import ArenaPreview from '../components/arena/ArenaPreview';
import DebateSubmission from '../components/arena/DebateSubmission';
import LiveDebate from '../components/arena/LiveDebate';

// API_BASE_URL is empty string to use relative paths (works on Vercel)
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

interface Debate {
  id: string;
  topic: string;
  status: 'active' | 'completed' | 'pending' | 'paused';
  pro_agent_name: string;
  con_agent_name: string;
  created_at: string;
  completed_at?: string;
  summary_pro?: string;
  summary_con?: string;
  summary_overall?: string;
  total_votes: number;
  pro_votes: number;
  con_votes: number;
  created_by?: string;
  is_public?: boolean;
}

interface DebateTurn {
  id: string;
  debate_id: string;
  turn_number: number;
  agent_side: 'pro' | 'con';
  phase: 'research' | 'opening' | 'rebuttal' | 'closing';
  content: string;
  research_sources?: any;
  created_at: string;
}

interface Vote {
  debate_id: string;
  user_id: string;
  vote_for: 'pro' | 'con';
}

const Arena: React.FC = () => {
  const { user } = useAuth();
  const [selectedDebate, setSelectedDebate] = useState<Debate | null>(null);
  const [debates, setDebates] = useState<Debate[]>([]);
  const [turns, setTurns] = useState<DebateTurn[]>([]);
  const [userVote, setUserVote] = useState<Vote | null>(null);
  const [isVoting, setIsVoting] = useState(false);
  const [remainingDebates, setRemainingDebates] = useState<number>(3);
  const [showDebateInterface, setShowDebateInterface] = useState(false);
  const [loadingDebates, setLoadingDebates] = useState<boolean>(true);
  
  
  // Streaming state for real-time updates (track per side to avoid flicker)
  const [streamingContent, setStreamingContent] = useState<{ pro: string; con: string }>({ pro: '', con: '' });
  
  // Track which debates we've connected to (to avoid duplicate connections)
  // Use ref instead of state to prevent infinite re-renders
  const connectedDebatesRef = useRef<Set<string>>(new Set());
  
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef<boolean>(true);

  // Token buffers live in a ref and are flushed to state at most once per
  // animation frame. Per-token flushSync caused a synchronous re-render for
  // every SSE chunk, which made the streaming view stutter and jump.
  const streamBuffersRef = useRef<{ pro: string; con: string }>({ pro: '', con: '' });
  const streamFlushRafRef = useRef<number | null>(null);

  const flushStreamBuffers = useCallback(() => {
    streamFlushRafRef.current = null;
    if (!isMountedRef.current) return;
    setStreamingContent({ ...streamBuffersRef.current });
  }, []);

  const scheduleStreamFlush = useCallback(() => {
    if (streamFlushRafRef.current === null) {
      streamFlushRafRef.current = requestAnimationFrame(flushStreamBuffers);
    }
  }, [flushStreamBuffers]);

  const resetStreamBuffers = useCallback((next: { pro: string; con: string } = { pro: '', con: '' }) => {
    streamBuffersRef.current = next;
    if (streamFlushRafRef.current !== null) {
      cancelAnimationFrame(streamFlushRafRef.current);
      streamFlushRafRef.current = null;
    }
    if (isMountedRef.current) {
      setStreamingContent({ ...next });
    }
  }, []);

  const fetchDebates = useCallback(async () => {
    try {
      setLoadingDebates(true);
      // Only fetch debates created by the current user
      let query = supabase
        .from('debates')
        .select('*')
        .order('created_at', { ascending: false });
      
      // Filter to only show user's own debates
      if (user) {
        query = query.eq('created_by', user.id);
      }

      const { data, error } = await query;

      if (error) throw error;
      setDebates(data || []);

      // Stabilize selection: keep current if present; otherwise choose first
      setSelectedDebate(prev => {
        if (!data || data.length === 0) return null;
        if (!prev) return data[0];
        const updated = data.find(d => d.id === prev.id);
        // Always return the updated object to force React re-render with new data
        if (updated) {
          // Create a new object reference to ensure React detects the change
          return { ...updated };
        }
        return prev;
      });
    } catch (error) {
      console.error('Error fetching debates:', error);
    }
    finally {
      setLoadingDebates(false);
    }
  }, [user]);

  const fetchUserDebateCount = useCallback(async () => {
    if (!user) return;

    try {
      // Use the new database function to get total available credits
      const { data, error } = await supabase
        .rpc('get_user_total_credits', { p_user_id: user.id });

      if (error) throw error;
      
      // data is the number of remaining credits
      setRemainingDebates(data || 0);
    } catch (error) {
      console.error('Error fetching user credits:', error);
    }
  }, [user]);

  const fetchDebateTurns = useCallback(async (debateId: string) => {
    try {
      console.log(`🔄 Fetching turns for debate ${debateId}...`);
      const { data, error } = await supabase
        .from('debate_turns')
        .select('*')
        .eq('debate_id', debateId)
        .order('turn_number', { ascending: true });

      if (error) throw error;
      
      console.log(`✅ Fetched ${data?.length || 0} turns for debate ${debateId}`);
      
      // Always set turns with fresh data to trigger re-render
      setTurns(data || []);
      
      return data || [];
    } catch (error) {
      console.error('Error fetching turns:', error);
      return [];
    }
  }, []);

  const fetchUserVote = useCallback(async (debateId: string) => {
    if (!user) return;

    try {
      console.log(`🗳️  Fetching vote for debate ${debateId}...`);
      const { data, error } = await supabase
        .from('votes')
        .select('*')
        .eq('debate_id', debateId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user vote:', error);
        return;
      }
      
      console.log(`✅ Vote fetched for debate ${debateId}:`, data ? `voted ${data.vote_for}` : 'no vote');
      setUserVote(data);
    } catch (error) {
      console.error('Error fetching user vote:', error);
    }
  }, [user]);


  // Define startDebateGeneration before it's used in useEffect
  const startDebateGeneration = useCallback(async (debateId: string) => {
    // Prevent duplicate connections
    if (connectedDebatesRef.current.has(debateId)) {
      console.log('⚠️ Already connected to debate:', debateId);
      return;
    }
    
    // Mark this debate as connected
    connectedDebatesRef.current.add(debateId);
    
    try {
      console.log('🚀 Starting streaming debate generation for:', debateId);
      
      // Clear any previous streaming content
      resetStreamBuffers();
      // Optimistically mark debate as active locally so UI shows streaming immediately
      setSelectedDebate(prev => prev ? { ...prev, status: 'active' } as Debate : prev);
      
      // Use fetch with streaming to handle Server-Sent Events  
      const response = await fetch(`${API_BASE_URL}/api/generate-debate-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ debate_id: debateId })
      });

      if (!response.ok || !response.body) {
        throw new Error(`Failed to start stream: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          console.log('✅ Stream complete');
          // Clear streaming buffers (all data should be in DB by now)
          resetStreamBuffers();
          // Fetch final state from database
          await fetchDebateTurns(debateId);
          await fetchDebates();
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        
        // Process complete messages (separated by \n\n)
        const messages = buffer.split('\n\n');
        buffer = messages.pop() || ''; // Keep incomplete message in buffer

        for (const message of messages) {
          const lines = message.split('\n');
          let eventType = '';
          let eventData: any = null;
          
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.substring(7).trim();
            } else if (line.startsWith('data: ')) {
              try {
                eventData = JSON.parse(line.substring(6));
              } catch (e) {
                console.warn('Failed to parse event data:', line);
              }
            }
          }
          
          // Handle different event types
          if (eventType && eventData) {
            switch (eventType) {
              case 'turn_start':
                console.log(`🎤 Starting ${eventData.side} ${eventData.phase}...`);
                resetStreamBuffers({
                  pro: eventData.side === 'pro' ? '' : streamBuffersRef.current.pro,
                  con: eventData.side === 'con' ? '' : streamBuffersRef.current.con,
                });
                break;

              case 'turn_stream':
                // Accumulate tokens in the ref; render at most once per frame.
                if (eventData.side === 'pro') {
                  streamBuffersRef.current.pro += eventData.content;
                } else {
                  streamBuffersRef.current.con += eventData.content;
                }
                scheduleStreamFlush();
                break;

              case 'turn_complete':
                console.log(`✅ ${eventData.side} turn complete`);
                // Keep the streamed text on screen until the saved turn arrives
                // from the DB, then clear — avoids a blank flash mid-swap.
                // eslint-disable-next-line no-loop-func
                fetchDebateTurns(debateId)
                  .then(() => {
                    resetStreamBuffers({
                      pro: eventData.side === 'pro' ? '' : streamBuffersRef.current.pro,
                      con: eventData.side === 'con' ? '' : streamBuffersRef.current.con,
                    });
                  })
                  .catch(console.error);
                break;

              case 'phase_start':
                console.log(`📋 Phase starting: ${eventData.name}`);
                break;

              case 'phase_complete':
                console.log(`✅ Phase complete: ${eventData.phase}`);
                // eslint-disable-next-line no-loop-func
                fetchDebateTurns(debateId)
                  .then(() => resetStreamBuffers())
                  .catch(console.error);
                setSelectedDebate(prev => prev ? { ...prev, status: 'active' } as Debate : prev);
                break;

              case 'summary_start':
                console.log('📝 Generating summary...');
                break;

              case 'summary_complete':
                console.log('✅ Summary complete!');
                // Awaiting is safe at the end — no more streaming chunks expected.
                await fetchDebates();
                await fetchDebateTurns(debateId);
                break;

              case 'complete':
                console.log('✅ Debate complete!');
                resetStreamBuffers();
                await Promise.all([fetchDebates(), fetchDebateTurns(debateId)]);
                setSelectedDebate(prev => prev ? { ...prev, status: 'completed' } as Debate : prev);
                break;

              case 'error':
                console.error('Error event:', eventData.message);
                resetStreamBuffers();
                throw new Error(eventData.message);
            }
          }
        }
      }
      
      // Remove from connected set now that generation is complete
      connectedDebatesRef.current.delete(debateId);
      
    } catch (error: any) {
      console.error('Error in streaming generation:', error);
      resetStreamBuffers();
      
      // Remove from connected set on error
      connectedDebatesRef.current.delete(debateId);
      
      alert(`Failed to start debate generation: ${error.message}`);
    }
  }, [fetchDebateTurns, fetchDebates, resetStreamBuffers, scheduleStreamFlush]);

  // Cleanup on unmount - set mounted flag to false
  useEffect(() => {
    isMountedRef.current = true;
    const connectedDebates = connectedDebatesRef.current;
    
    return () => {
      isMountedRef.current = false;
      // Clear any polling/streaming connections
      connectedDebates.clear();
      if (streamFlushRafRef.current !== null) {
        cancelAnimationFrame(streamFlushRafRef.current);
      }
    };
  }, []);

  // Fetch debates and user debate count on mount and when user changes
  useEffect(() => {
    fetchDebates();
    if (user) {
      fetchUserDebateCount();
    } else {
      setSelectedDebate(null);
      setDebates([]);
    }
  }, [user, fetchDebates, fetchUserDebateCount]);

  // Fetch turns and vote when debate is selected
  useEffect(() => {
    if (selectedDebate) {
      console.log(`📊 Loading debate: ${selectedDebate.id}`);
      // Clear previous debate data immediately to prevent flicker/wrong data
      setTurns([]);
      setUserVote(null);
      resetStreamBuffers();
      
      // Fetch new debate data
      fetchDebateTurns(selectedDebate.id);
      if (user) {
        fetchUserVote(selectedDebate.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDebate?.id, user]);

  // Auto-reconnect to active debates (e.g., after page refresh)
  useEffect(() => {
    if (selectedDebate && (selectedDebate.status === 'active' || selectedDebate.status === 'pending')) {
      // Only connect if we haven't already connected to this debate
      if (!connectedDebatesRef.current.has(selectedDebate.id)) {
        console.log('🔄 Auto-reconnecting to active debate:', selectedDebate.id);
        startDebateGeneration(selectedDebate.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDebate?.id, selectedDebate?.status, startDebateGeneration]);

  const handleDebateSubmission = async (topic: string) => {
    if (!user || remainingDebates <= 0) return;

    try {
      // Import moderation service
      const { moderateContent, getModerationMessage, quickLocalCheck } = await import('../services/moderation');
      
      // Step 1: Quick local validation
      const localCheck = quickLocalCheck(topic);
      if (localCheck.blocked) {
        alert(localCheck.reason);
        return;
      }

      // Step 2: Check content with OpenAI Moderation API
      console.log('Checking content moderation for topic...');
      const moderationResult = await moderateContent(topic);
      
      if (moderationResult.flagged) {
        const message = getModerationMessage(moderationResult);
        alert(`⚠️ Content Moderation Alert\n\n${message}\n\nPlease create a topic that promotes respectful and constructive debate.`);
        return;
      }

      console.log('Content moderation passed ✓');

      // Step 3: Use a credit for debate creation
      const { data: creditUsed, error: creditError } = await supabase
        .rpc('use_credit', { p_user_id: user.id, p_transaction_type: 'debate_creation' });

      if (creditError) {
        console.error('Error using credit:', creditError);
        throw new Error('Failed to use credit. Please try again.');
      }

      if (!creditUsed) {
        alert('You have no remaining credits available.');
        return;
      }

      console.log('✅ Credit successfully used for debate creation');

      // Step 4: Generate dynamic labels based on topic
      const { generateDebateLabels } = await import('../utils/debateLabels');
      const labels = generateDebateLabels(topic);
      
      // Step 5: Create new debate
      const { data: newDebate, error } = await supabase
        .from('debates')
        .insert({
          topic,
          status: 'pending',
          created_by: user.id,
          is_public: false, // Debates are private by default
          pro_agent_name: labels.proLabel,
          con_agent_name: labels.conLabel
        })
        .select()
        .single();

      if (error) throw error;

      // Refresh debates and counts
      await fetchDebates();
      await fetchUserDebateCount();

      // Start the debate generation on backend
      if (newDebate) {
        setSelectedDebate(newDebate);
        setShowDebateInterface(false);
        setTurns([]);
        
        // Trigger backend streaming generation (SSE will handle everything)
        startDebateGeneration(newDebate.id);
      }
    } catch (error) {
      console.error('Error creating debate:', error);
      alert('Failed to create debate. Please try again.');
    }
  };

  

  

  const handleRegenerate = async () => {
    if (!selectedDebate || !user) return;

    // Check if user has credits available
    if (remainingDebates <= 0) {
      alert('You have no remaining credits. Regenerating a debate costs 1 credit.');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to regenerate this debate? This will cost 1 credit and delete all current turns.\n\nRemaining credits: ${remainingDebates}`
    );

    if (!confirmed) return;

    try {
      console.log('🔄 Starting debate regeneration...');
      
      // Step 1: Use a credit for regeneration
      const { data: creditUsed, error: creditError } = await supabase
        .rpc('use_credit', { p_user_id: user.id, p_transaction_type: 'regeneration' });

      if (creditError) {
        console.error('Error using regeneration credit:', creditError);
        throw new Error('Failed to use credit. Please try again.');
      }

      if (!creditUsed) {
        alert('You have no remaining credits available.');
        return;
      }

      console.log('✅ Credit successfully used for regeneration');
      
      // Update the remaining debates count in UI
      await fetchUserDebateCount();
      
      // Step 2: Clear UI immediately
      setTurns([]);
      
      // Step 3: Delete all existing turns
      console.log('🗑️  Deleting existing turns...');
      const { error: deleteError } = await supabase
        .from('debate_turns')
        .delete()
        .eq('debate_id', selectedDebate.id);

      if (deleteError) {
        console.error('Error deleting turns:', deleteError);
        throw deleteError;
      }
      console.log('✅ Deleted existing turns');

      // Step 4: Reset debate to pending status
      console.log('🔄 Resetting debate status...');
      const { error: updateError } = await supabase
        .from('debates')
        .update({ 
          status: 'pending',
          summary_pro: null,
          summary_con: null,
          summary_overall: null,
          completed_at: null
        })
        .eq('id', selectedDebate.id);

      if (updateError) {
        console.error('Error updating debate:', updateError);
        throw updateError;
      }
      console.log('✅ Debate status reset to pending');

      // Step 5: Update local state
      setSelectedDebate({
        ...selectedDebate,
        status: 'pending',
        summary_pro: undefined,
        summary_con: undefined,
        summary_overall: undefined,
        completed_at: undefined
      });

      // Step 6: Clear any previous connection tracking
      connectedDebatesRef.current.delete(selectedDebate.id);

      // Step 7: Start generation
      console.log('🚀 Starting fresh debate...');
      await startDebateGeneration(selectedDebate.id);
    } catch (error) {
      console.error('Error regenerating debate:', error);
      alert('Failed to regenerate debate. Please try again.');
    }
  };

  const handleVote = async (voteFor: 'pro' | 'con') => {
    if (!user || !selectedDebate || isVoting) return;

    setIsVoting(true);
    const previousVote = userVote;
    const debateId = selectedDebate.id;

    // Compute the next counts immutably from the currently selected debate so
    // the tiles update the instant you click — no network roundtrip, no jump.
    const computeCounts = (d: { pro_votes: number; con_votes: number; total_votes: number }) => {
      let pro = d.pro_votes || 0;
      let con = d.con_votes || 0;
      let total = d.total_votes || 0;
      if (previousVote) {
        // Switching sides: move one vote across, total unchanged
        if (previousVote.vote_for === 'pro' && voteFor === 'con') {
          pro = Math.max(0, pro - 1);
          con += 1;
        } else if (previousVote.vote_for === 'con' && voteFor === 'pro') {
          con = Math.max(0, con - 1);
          pro += 1;
        }
      } else {
        // First vote: add to the chosen side and the total
        if (voteFor === 'pro') pro += 1;
        else con += 1;
        total += 1;
      }
      return { pro_votes: pro, con_votes: con, total_votes: total };
    };

    try {
      // Optimistically update user vote and local counts
      setUserVote({ debate_id: debateId, user_id: user.id, vote_for: voteFor });
      setSelectedDebate(prev =>
        prev && prev.id === debateId ? { ...prev, ...computeCounts(prev) } : prev,
      );
      setDebates(prev => prev.map(d => (d.id === debateId ? { ...d, ...computeCounts(d) } : d)));

      if (previousVote) {
        // Update existing vote
        await supabase
          .from('votes')
          .update({ vote_for: voteFor })
          .eq('debate_id', selectedDebate.id)
          .eq('user_id', user.id);
      } else {
        // Create new vote
        await supabase
          .from('votes')
          .insert({
            debate_id: selectedDebate.id,
            user_id: user.id,
            vote_for: voteFor
          });
      }

      // Best-effort refresh in background to sync with server counts
      fetchDebates();
    } catch (error) {
      console.error('Error voting:', error);
    } finally {
      setIsVoting(false);
    }
  };

  const handleMakePublic = async () => {
    if (!user || !selectedDebate) return;

    try {
      // Re-check content moderation before making public
      const { moderateContent, getModerationMessage } = await import('../services/moderation');
      
      console.log('Re-checking content moderation before making public...');
      const moderationResult = await moderateContent(selectedDebate.topic);
      
      if (moderationResult.flagged) {
        const message = getModerationMessage(moderationResult);
        alert(`⚠️ Cannot Make Public\n\n${message}\n\nThis debate cannot be shared publicly due to content policy violations.`);
        return;
      }

      console.log('Content moderation passed for public sharing ✓');

      const { error } = await supabase
        .from('debates')
        .update({ is_public: true })
        .eq('id', selectedDebate.id)
        .eq('created_by', user.id);

      if (error) throw error;

      // Refresh debates to get updated status
      await fetchDebates();
      
      // Show success message
      alert('✅ Your debate is now public and visible in the Explore page!');
    } catch (error) {
      console.error('Error making debate public:', error);
      alert('Failed to make debate public. Please try again.');
    }
  };

  const handleDebateSelection = useCallback((debateId: string) => {
    const nextDebate = debates.find(d => d.id === debateId);
    if (!nextDebate || nextDebate.id === selectedDebate?.id) return;
    
    console.log(`🔄 Switching to debate: ${nextDebate.topic}`);
    
    // Disconnect from any active streaming for the previous debate
    if (selectedDebate) {
      connectedDebatesRef.current.delete(selectedDebate.id);
    }
    
    // Set the new debate (useEffect will handle clearing old data and fetching new)
    setSelectedDebate(nextDebate);
  }, [debates, selectedDebate]);

  const handleDeleteDebate = async (debateId: string) => {
    if (!user) return;

    const debateToDelete = debates.find(d => d.id === debateId);
    if (!debateToDelete) return;

    // Build confirmation message
    let confirmMessage = `Are you sure you want to delete this debate?\n\n`;
    confirmMessage += `Topic: "${debateToDelete.topic}"\n`;
    confirmMessage += `Status: ${debateToDelete.status}\n`;
    confirmMessage += `Votes: ${debateToDelete.total_votes}\n\n`;
    
    // Extra warning for public debates with votes
    if (debateToDelete.is_public && debateToDelete.total_votes > 0) {
      confirmMessage += `⚠️ WARNING: This is a public debate with ${debateToDelete.total_votes} vote(s).\n`;
      confirmMessage += `Deleting it will remove it from the Explore page and all votes will be lost.\n\n`;
    }
    
    confirmMessage += `This action cannot be undone. All debate content, turns, and votes will be permanently deleted.`;

    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    try {
      // Delete the debate (cascade will handle turns, votes, leaderboard cache)
      const { error } = await supabase
        .from('debates')
        .delete()
        .eq('id', debateId)
        .eq('created_by', user.id); // Extra safety check

      if (error) throw error;

      console.log('✅ Debate deleted successfully');

      // If deleted debate was selected, clear selection
      if (selectedDebate?.id === debateId) {
        setSelectedDebate(null);
        setTurns([]);
        setUserVote(null);
        resetStreamBuffers();
        connectedDebatesRef.current.delete(debateId);
      }

      // Refresh debates list
      await fetchDebates();
      
      alert('✅ Debate deleted successfully');
      
    } catch (error) {
      console.error('Error deleting debate:', error);
      alert('Failed to delete debate. Please try again.');
    }
  };

  // Show preview for non-authenticated users
  if (!user) {
    return <ArenaPreview />;
  }

  // Calculate current phase for display
  const getCurrentPhase = () => {
    if (!selectedDebate) return '';
    if (selectedDebate.status === 'completed') return '';
    if (selectedDebate.status === 'paused') return 'Paused';
    if (selectedDebate.status === 'pending') return 'Starting debate...';
    
    const turnCount = turns.length;
    if (turnCount < 2) return 'Research Phase';
    if (turnCount < 4) return 'Opening Statements';
    if (turnCount < 6) return 'Rebuttals';
    if (turnCount < 8) return 'Closing Arguments';
    return 'Generating Summary';
  };

  const isGenerating = selectedDebate?.status === 'active' || selectedDebate?.status === 'pending';

  if (loadingDebates) {
    return (
      <div className="relative isolate flex min-h-screen w-full items-center justify-center bg-white">
        <div aria-hidden className="blueprint-grid pointer-events-none absolute inset-0 z-0" />
        <div className="relative z-10 flex flex-col items-center gap-4">
          <div className="relative h-8 w-8">
            <div className="absolute inset-0 rounded-full border-2 border-hairline" />
            <div className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-charcoal" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative isolate min-h-screen w-full bg-white">
      {/* Blueprint grid */}
      <div aria-hidden className="blueprint-grid pointer-events-none absolute inset-0 z-0" />

      <div className="relative z-10">
        {/* Header */}
        <section className="border-b border-hairline">
          <div className="mx-auto max-w-7xl px-6 py-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                <span className="eyebrow">Your arena</span>
                <h1 className="mt-2 font-serif text-4xl tracking-tight text-charcoal">
                  Debate arena
                </h1>
                <p className="mt-1 text-sm text-ink-muted">
                  Create private debates, watch AI argue both sides, then share publicly.
                </p>
              </motion.div>

              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                onClick={() => setShowDebateInterface(true)}
                className="btn-primary flex-shrink-0 self-start !px-5"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New debate
              </motion.button>
            </div>

            {/* Stats strip */}
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mt-8 grid grid-cols-3 gap-4"
            >
              {[
                { label: 'Your debates', value: debates.length },
                { label: 'Public', value: debates.filter(d => d.is_public === true).length },
                { label: 'Credits left', value: remainingDebates },
              ].map(stat => (
                <div
                  key={stat.label}
                  className="rounded-xl border border-black/[0.07] bg-white/90 px-5 py-4 backdrop-blur-sm"
                >
                  <p className="font-serif text-3xl text-charcoal">{stat.value}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-ink-muted">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Main content */}
        <div className="mx-auto max-w-7xl px-6 py-10">
          {showDebateInterface ? (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <div className="mb-6">
                <button
                  onClick={() => setShowDebateInterface(false)}
                  className="inline-flex items-center gap-2 text-sm font-medium text-ink-muted transition-colors hover:text-charcoal"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to debates
                </button>
              </div>
              <DebateSubmission
                onSubmit={handleDebateSubmission}
                isDisabled={remainingDebates <= 0}
                remainingDebates={remainingDebates}
              />
            </motion.div>

          ) : selectedDebate ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {/* Debate nav bar */}
              <div className="mb-6">
                <div className="flex items-center justify-between gap-4 rounded-xl border border-black/[0.07] bg-white/90 p-4 shadow-frame backdrop-blur-sm">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    {/* Status */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className={`h-2 w-2 rounded-full ${
                        selectedDebate.status === 'completed' ? 'bg-charcoal/35' :
                        selectedDebate.status === 'active' ? 'bg-steel-blue animate-pulse' :
                        selectedDebate.status === 'paused' ? 'bg-charcoal/20' :
                        'bg-charcoal/20 animate-pulse'
                      }`} />
                      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted whitespace-nowrap">
                        {selectedDebate.status === 'completed' ? 'Complete' :
                         selectedDebate.status === 'active' ? 'Live' :
                         selectedDebate.status === 'paused' ? 'Paused' : 'Starting'}
                      </span>
                    </div>

                    <span className="font-mono text-[10px] text-ink-muted whitespace-nowrap flex-shrink-0">
                      · {selectedDebate.is_public ? 'Public' : 'Private'}
                    </span>
                    <span className="font-mono text-[10px] text-ink-muted whitespace-nowrap flex-shrink-0">
                      · {selectedDebate.total_votes} {selectedDebate.total_votes === 1 ? 'vote' : 'votes'}
                    </span>

                    {debates.length > 1 && (
                      <>
                        <div className="mx-1 h-4 w-px bg-hairline flex-shrink-0" />
                        <select
                          value={selectedDebate?.id || ''}
                          onChange={e => handleDebateSelection(e.target.value)}
                          className="min-w-0 flex-1 rounded-lg border border-black/[0.08] bg-white px-3 py-1.5 text-xs text-charcoal focus:border-charcoal/30 focus:outline-none focus:ring-2 focus:ring-charcoal/10"
                        >
                          {debates.map(debate => (
                            <option key={debate.id} value={debate.id}>
                              {debate.topic.length > 60
                                ? debate.topic.substring(0, 60) + '…'
                                : debate.topic}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>

                  <button
                    onClick={() => handleDeleteDebate(selectedDebate.id)}
                    className="flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-black/[0.08] px-3 py-1.5 text-xs font-medium text-ink-muted transition-colors hover:border-charcoal/20 hover:text-charcoal"
                    title="Delete this debate"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span className="hidden sm:inline">Delete</span>
                  </button>
                </div>
              </div>

              {/* Live debate viewer */}
              <LiveDebate
                topic={selectedDebate.topic}
                turns={turns}
                currentPhase={getCurrentPhase()}
                streamingContent={streamingContent}
                isGenerating={isGenerating}
                onRegenerate={handleRegenerate}
                showControls={true}
                hasSummary={!!(selectedDebate.summary_pro || selectedDebate.summary_con || selectedDebate.summary_overall)}
                summaryText={selectedDebate.summary_overall || ''}
                proLabel="Supporting"
                conLabel="Opposing"
              />

              {/* Voting — only when complete */}
              {selectedDebate.status === 'completed' && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-8 overflow-hidden rounded-2xl border border-black/[0.07] bg-white/90 p-8 shadow-frame backdrop-blur-sm"
                >
                  <span className="text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
                    Your vote
                  </span>
                  <h3 className="mt-2 font-serif text-2xl text-charcoal">
                    Which side made the stronger case?
                  </h3>

                  {/* Vote buttons — For left, Against right */}
                  <div className="mt-7 grid grid-cols-1 gap-4 md:grid-cols-2">
                    {/* Supporting (pro) */}
                    <button
                      onClick={() => handleVote('pro')}
                      disabled={isVoting}
                      className={`rounded-2xl border-2 p-6 text-left transition-[background-color,border-color,box-shadow] duration-300 ease-out-expo ${
                        userVote?.vote_for === 'pro'
                          ? 'border-steel-blue bg-steel-blue shadow-frame'
                          : 'border-hairline bg-white hover:border-steel-blue/40 hover:bg-steel-blue/[0.03]'
                      }`}
                    >
                      <div className="mb-3 flex items-center gap-2">
                        <span className={`h-3 w-1 flex-shrink-0 rounded-full ${userVote?.vote_for === 'pro' ? 'bg-white/50' : 'bg-steel-blue'}`} />
                        <span className={`font-mono text-xs font-medium uppercase tracking-wider ${userVote?.vote_for === 'pro' ? 'text-white/80' : 'text-steel-blue'}`}>
                          Supporting
                        </span>
                      </div>
                      <p className={`font-serif text-3xl font-semibold ${userVote?.vote_for === 'pro' ? 'text-white' : 'text-charcoal'}`}>
                        {selectedDebate.pro_votes}
                      </p>
                      <p className={`mt-1 font-mono text-xs ${userVote?.vote_for === 'pro' ? 'text-white/70' : 'text-ink-muted'}`}>
                        {selectedDebate.total_votes > 0
                          ? `${Math.round((selectedDebate.pro_votes / selectedDebate.total_votes) * 100)}% of votes`
                          : '—'}
                      </p>
                    </button>

                    {/* Against (con) */}
                    <button
                      onClick={() => handleVote('con')}
                      disabled={isVoting}
                      className={`rounded-2xl border-2 p-6 text-left transition-[background-color,border-color,box-shadow] duration-300 ease-out-expo ${
                        userVote?.vote_for === 'con'
                          ? 'border-vermilion bg-vermilion shadow-frame'
                          : 'border-hairline bg-white hover:border-vermilion/40 hover:bg-vermilion/[0.03]'
                      }`}
                    >
                      <div className="mb-3 flex items-center gap-2">
                        <span className={`h-3 w-1 flex-shrink-0 rounded-full ${userVote?.vote_for === 'con' ? 'bg-white/50' : 'bg-vermilion'}`} />
                        <span className={`font-mono text-xs font-medium uppercase tracking-wider ${userVote?.vote_for === 'con' ? 'text-white/80' : 'text-vermilion'}`}>
                          Against
                        </span>
                      </div>
                      <p className={`font-serif text-3xl font-semibold ${userVote?.vote_for === 'con' ? 'text-white' : 'text-charcoal'}`}>
                        {selectedDebate.con_votes}
                      </p>
                      <p className={`mt-1 font-mono text-xs ${userVote?.vote_for === 'con' ? 'text-white/70' : 'text-ink-muted'}`}>
                        {selectedDebate.total_votes > 0
                          ? `${Math.round((selectedDebate.con_votes / selectedDebate.total_votes) * 100)}% of votes`
                          : '—'}
                      </p>
                    </button>
                  </div>

                  {/* Voted confirmation — fixed slot so the layout never jumps */}
                  <div className="mt-5 min-h-[1.25rem]">
                    {userVote && (
                      <p className="text-xs text-ink-muted">
                        You voted for the{' '}
                        <span className="font-semibold text-charcoal">
                          {userVote.vote_for === 'pro' ? 'supporting' : 'opposing'}
                        </span>{' '}
                        argument — tap the other side to change it.
                      </p>
                    )}
                  </div>

                  {/* Make public */}
                  {selectedDebate.created_by === user.id && !selectedDebate.is_public && (
                    <div className="mt-8 border-t border-hairline pt-8">
                      <div className="flex flex-col gap-4 rounded-xl border border-black/[0.07] bg-white/90 p-6 backdrop-blur-sm sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <h4 className="font-serif text-lg text-charcoal">Share this debate</h4>
                          <p className="mt-1 text-pretty text-sm text-ink-muted">
                            Make it public so the community can watch and vote. It'll appear on the Explore page.
                          </p>
                        </div>
                        <Button onClick={handleMakePublic} variant="primary" size="medium">
                          Make public
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Public status */}
                  {selectedDebate.is_public && (
                    <div className="mt-8 border-t border-hairline pt-8">
                      <div className="flex items-center justify-between gap-4 rounded-xl border border-black/[0.07] bg-white/90 px-5 py-4 backdrop-blur-sm">
                        <div className="flex items-center gap-3">
                          <span className="h-2 w-2 rounded-full bg-charcoal/30" />
                          <div>
                            <p className="text-sm font-semibold text-charcoal">Publicly visible</p>
                            <p className="text-xs text-ink-muted">
                              Showing on the Explore page for community voting
                            </p>
                          </div>
                        </div>
                        {selectedDebate.created_by === user.id && (
                          <Button
                            onClick={async () => {
                              try {
                                await supabase
                                  .from('debates')
                                  .update({ is_public: false })
                                  .eq('id', selectedDebate.id)
                                  .eq('created_by', user.id);
                                await fetchDebates();
                              } catch (e) {
                                console.error('Error making debate private:', e);
                                alert('Failed to make debate private. Please try again.');
                              }
                            }}
                            variant="secondary"
                            size="small"
                          >
                            Make private
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>

          ) : (
            /* Empty state */
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="mx-auto max-w-2xl py-20 text-center"
            >
              <div className="overflow-hidden rounded-2xl border border-black/[0.07] bg-white/90 p-12 shadow-frame backdrop-blur-sm">
                <h2 className="font-serif text-3xl tracking-tight text-charcoal">
                  Welcome to your arena.
                </h2>
                <p className="mx-auto mt-3 max-w-md text-pretty text-base leading-relaxed text-ink-muted">
                  Create your first debate and watch AI argue both sides of any question.
                </p>

                <div className="mt-10 divide-y divide-hairline text-left">
                  {[
                    { n: '01', title: 'Choose a topic', body: 'Pick any balanced question or debate topic.' },
                    { n: '02', title: 'Watch AI debate', body: 'AI agents research and argue both sides in real-time.' },
                    { n: '03', title: 'Share publicly', body: 'Make your debate public for community voting.' },
                  ].map(step => (
                    <div key={step.n} className="flex items-baseline gap-4 py-5 first:pt-0">
                      <span className="w-7 flex-shrink-0 font-serif text-lg text-charcoal/25">{step.n}</span>
                      <div>
                        <h4 className="text-sm font-semibold text-charcoal">{step.title}</h4>
                        <p className="mt-0.5 text-xs text-ink-muted">{step.body}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setShowDebateInterface(true)}
                  className="btn-primary mt-10 w-full !py-4"
                >
                  Start your first debate
                </button>
                <p className="mt-4 font-mono text-[11px] uppercase tracking-wider text-ink-muted">
                  {remainingDebates} credit{remainingDebates !== 1 ? 's' : ''} available
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Arena;

